import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { array, storage } from 'app/utils';
import { fetchLookup, getLookup, useGlobalSelector, useGlobalStore, useThunkDispatch, IFetchOptions } from 'app/state';
import { utils } from 'app/state/lookup';
import { ISource } from 'app/components';
import { ILookup } from 'app/models';

export const useSignal = <T>(initial: T): (next: T) => T => {
  const ref = useRef(initial);

  return useCallback((next: T) => {
    const prev = ref.current;
    ref.current = next;

    return prev;
  }, []);
};

export const useStorageState = <S>(key: string, initial: S | (() => S)): [S, Dispatch<SetStateAction<S>>] => {
  const [state, setState] = useState<S>(storage.get(key) ?? initial);

  const setStorageState = useCallback((value: SetStateAction<S>): void => {
    if (value instanceof Function) value = value(state);
    storage.set(key, value);

    return setState(value);
  }, [key, state]);

  return [state, setStorageState];
};

export const useLookup = <T extends ILookup = ILookup>(fetch: string | IFetchOptions): T[] => {
  if (typeof fetch === 'string') fetch = { key: fetch };

  const { key, params } = fetch;
  const dispatch = useThunkDispatch();

  useEffect(() => {
    dispatch(fetchLookup(fetch));
  }, [dispatch, fetch]);

  return useGlobalSelector(state => (getLookup(state, utils.encode(key, params)) ?? [])) as T[];
};

export const useDictionary = <T extends ILookup = ILookup>(fetch: string | IFetchOptions, key: string = 'value'): Record<any, T> => {
  return array.collect(useLookup(fetch), o => o[key]);
};

export interface ISourceState<T> {
  loading: boolean;
  options: T[];
}

export const useSource = <T extends ILookup = ILookup>(source: ISource<T>, defaults?: Partial<ISourceState<T>>): ISourceState<T> => {
  const store = useGlobalStore(), dispatch = useThunkDispatch();
  const [loading, setLoading] = useState(false);

  const sources: Record<ISource<T>['type'], ((source: ISource<T>) => T[] | undefined)> = useMemo(() => ({
    'lookup': ({ key, params }) => (key ? getLookup(store.getState(), utils.encode(key, params)) as T[] : undefined),
    'static': ({ data }) => data,
  }), [store]);

  const { type, key, params, filter } = source;
  const data = (sources[type](source) ?? defaults?.options);

  useEffect(() => {
    if (data || type !== 'lookup' || !key) return;

    setLoading(true);
    dispatch(fetchLookup({ key, params })).then(() => setLoading(false));
  }, [dispatch, type, key, params, data]);


  return {
    loading: (defaults?.loading || loading),
    options: ((filter ? data?.filter(filter) : data) ?? []),
  };
}
